import type { FormEvent, JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  MODULES_ADDONS,
  MODULES_COMPLEMENTAIRES,
  PALIERS,
  REGISTRE_MODULES,
  dependancesManquantes,
  modulesDependantDe,
  type Module,
  type ModuleAddon,
  type Palier,
} from '@artosera/shared';
import { Badge, Button, Checkbox, Input, Select, Textarea } from '@artosera/ui';
import { useLibellesModules } from '../../../lib/modules.js';
import { analyserMontant, montantEnSaisie } from '../../../lib/montant.js';
import { dateEnSaisie } from '../../../lib/ouvertures.js';
import type { OffreOuverture } from '../../../types.js';
import styles from '../Ouvertures.module.css';
import { Champ, descriptionChamp, type PropsEtape } from './commun.js';

type ModeDroit = OffreOuverture['addons'][number]['mode'];
interface DroitSaisi {
  mode: ModeDroit;
  /** `AAAA-MM-JJ` ; vide pour un achat. */
  jusquAu: string;
}

/** Fin de journée UTC : un add-on loué « jusqu'au » une date court toute cette date. */
function finDeJournee(jour: string): string {
  return `${jour}T23:59:59.000Z`;
}

const SERVICES = ['site', 'boutique'] as const;

/** Étape 2 : palier, modules complémentaires, add-ons, services et forfait de reprise. */
export function EtapeOffre({
  dossier,
  enCours,
  lectureSeule,
  sauver,
  allerA,
}: PropsEtape): JSX.Element {
  const { t } = useTranslation('console');
  const libelles = useLibellesModules();
  const offre = dossier?.offre;

  const [palier, setPalier] = useState<Palier>(offre?.palier ?? 'galerie');
  const [modules, setModules] = useState<Set<string>>(new Set(offre?.modules ?? []));
  const [addons, setAddons] = useState<Map<string, DroitSaisi>>(
    new Map(
      (offre?.addons ?? []).map((droit) => [
        droit.addon,
        { mode: droit.mode, jusquAu: dateEnSaisie(droit.jusquAu) },
      ]),
    ),
  );
  const [services, setServices] = useState<Set<string>>(new Set(offre?.services ?? []));
  const [forfaitTexte, setForfaitTexte] = useState(offre?.forfaitReprise.texte ?? '');
  const [forfaitMontant, setForfaitMontant] = useState(
    montantEnSaisie(offre?.forfaitReprise.montantCentimes ?? null),
  );
  const [erreurs, setErreurs] = useState<Record<string, string>>({});

  const basculerModule = (module: string, actif: boolean): void =>
    setModules((courant) => {
      const suivant = new Set(courant);
      if (actif) suivant.add(module);
      else suivant.delete(module);
      return suivant;
    });

  const basculerAddon = (addon: string, actif: boolean): void =>
    setAddons((courant) => {
      const suivant = new Map(courant);
      if (actif) suivant.set(addon, { mode: 'achat', jusquAu: '' });
      else suivant.delete(addon);
      return suivant;
    });

  const changerDroit = (addon: string, changement: Partial<DroitSaisi>): void =>
    setAddons((courant) => {
      const actuel = courant.get(addon);
      if (!actuel) return courant;
      const suivant = new Map(courant);
      const fusion = { ...actuel, ...changement };
      // Un achat n'a pas d'échéance.
      suivant.set(addon, fusion.mode === 'achat' ? { ...fusion, jusquAu: '' } : fusion);
      return suivant;
    });

  /** Les dépendances d'un module ou d'un add-on que la sélection ne couvre pas. */
  const manquantes = (module: string, selection: Iterable<string>): readonly Module[] =>
    dependancesManquantes(module as Module, [...selection]);
  const listeLibelles = (liste: readonly string[]): string => libelles.liste(liste);

  const valider = (): Record<string, string> => {
    const prochaines: Record<string, string> = {};
    for (const module of modules) {
      const absentes = manquantes(module, modules);
      if (absentes.length > 0) {
        prochaines[`module-${module}`] = t('ouvertures.offre.dependanceManquante', {
          liste: listeLibelles(absentes),
        });
      }
    }
    for (const [addon, droit] of addons) {
      const absentes = manquantes(addon, addons.keys());
      if (absentes.length > 0) {
        prochaines[`addon-${addon}`] = t('ouvertures.offre.dependanceManquante', {
          liste: listeLibelles(absentes),
        });
      }
      if (droit.mode !== 'achat') {
        const fin = droit.jusquAu === '' ? NaN : new Date(finDeJournee(droit.jusquAu)).getTime();
        if (Number.isNaN(fin) || fin <= Date.now()) {
          prochaines[`echeance-${addon}`] = t('ouvertures.offre.erreurEcheance', {
            addon: libelles.libelle(addon),
          });
        }
      }
    }
    if (!analyserMontant(forfaitMontant).ok) {
      prochaines.forfaitMontant = t('ouvertures.offre.erreurMontant');
    }
    return prochaines;
  };

  const surEnvoi = (evenement: FormEvent): void => {
    evenement.preventDefault();
    const prochaines = valider();
    setErreurs(prochaines);
    if (Object.keys(prochaines).length > 0) return;
    const montant = analyserMontant(forfaitMontant);
    void sauver(
      {
        offre: {
          palier,
          modules: [...modules],
          addons: [...addons].map(([addon, droit]) => ({
            addon,
            mode: droit.mode,
            jusquAu: droit.mode === 'achat' ? null : finDeJournee(droit.jusquAu),
          })),
          services: SERVICES.filter((service) => services.has(service)),
          forfaitReprise: {
            texte: forfaitTexte.trim(),
            montantCentimes: montant.ok ? montant.valeur : null,
          },
        },
      },
      dossier?.identite.estTest ? 6 : 3,
    );
  };

  const ligneAddon = (addon: ModuleAddon): JSX.Element => {
    const disponible = REGISTRE_MODULES[addon].disponible;
    const droit = addons.get(addon);
    const requis = dependancesManquantes(addon, []);
    const dependants = modulesDependantDe(addon).filter((autre) => addons.has(autre));
    const erreurDependance = erreurs[`addon-${addon}`];
    const erreurEcheance = erreurs[`echeance-${addon}`];
    return (
      <li
        key={addon}
        className={`${styles.choix} ${droit ? styles.choixCoche : ''}`}
        data-addon={addon}
      >
        <div className={styles.choixTete}>
          <Checkbox
            id={`ouv-addon-${addon}`}
            label={libelles.libelle(addon)}
            checked={droit !== undefined}
            disabled={!disponible || (droit !== undefined && dependants.length > 0)}
            onCheckedChange={(valeur) => basculerAddon(addon, valeur === true)}
          />
          {!disponible ? <Badge>{t('ouvertures.offre.bientot')}</Badge> : null}
        </div>
        <p className={styles.choixAide}>{libelles.description(addon)}</p>
        {requis.length > 0 ? (
          <p className={styles.choixAide}>
            {t('ouvertures.offre.necessite', { liste: listeLibelles(requis) })}
          </p>
        ) : null}
        {droit ? (
          <div className={styles.choixDetails}>
            <Champ id={`ouv-mode-${addon}`} label={t('ouvertures.offre.mode')}>
              <Select
                id={`ouv-mode-${addon}`}
                value={droit.mode}
                onValueChange={(valeur) => changerDroit(addon, { mode: valeur as ModeDroit })}
                options={modesProposes(droit.mode).map((mode) => ({
                  value: mode,
                  label: t(`ouvertures.offre.modes.${mode}`),
                }))}
              />
            </Champ>
            {droit.mode !== 'achat' ? (
              <Champ
                id={`ouv-echeance-${addon}`}
                label={t('ouvertures.offre.echeance')}
                aide={t('ouvertures.offre.echeanceAide')}
                erreur={erreurEcheance}
              >
                <Input
                  id={`ouv-echeance-${addon}`}
                  type="date"
                  mono
                  value={droit.jusquAu}
                  onChange={(evenement) => changerDroit(addon, { jusquAu: evenement.target.value })}
                  {...descriptionChamp(`ouv-echeance-${addon}`, {
                    aide: true,
                    erreur: Boolean(erreurEcheance),
                  })}
                />
              </Champ>
            ) : null}
          </div>
        ) : null}
        {erreurDependance ? (
          <p className={styles.choixAlerte} role="alert">
            {erreurDependance}
          </p>
        ) : null}
      </li>
    );
  };

  const addonsLivres = MODULES_ADDONS.filter(
    (addon) => REGISTRE_MODULES[addon].disponible || addons.has(addon),
  );
  const addonsAVenir = MODULES_ADDONS.filter((addon) => !addonsLivres.includes(addon));

  const modesProposes = (courant: ModeDroit): ModeDroit[] =>
    courant === 'essai' ? ['achat', 'location', 'essai'] : ['achat', 'location'];

  return (
    <form className={styles.etape} onSubmit={surEnvoi} noValidate>
      <header>
        <h2>{t('ouvertures.offre.titre')}</h2>
        <p>{t('ouvertures.offre.texte')}</p>
      </header>

      <fieldset className={styles.groupe} disabled={lectureSeule}>
        <div className={styles.groupeGrille}>
          <Champ
            id="ouv-palier"
            label={t('ouvertures.offre.palier')}
            aide={t('ouvertures.offre.palierAide')}
          >
            <Select
              id="ouv-palier"
              value={palier}
              onValueChange={(valeur) => setPalier(valeur as Palier)}
              aria-describedby="ouv-palier-aide"
              options={PALIERS.map((valeur) => ({
                value: valeur,
                label: t(`galeries.palier.${valeur}`),
              }))}
            />
          </Champ>
        </div>

        <hr className={styles.separateur} />

        <h3>{t('ouvertures.offre.modules')}</h3>
        <p className={styles.groupeTexte}>{t('ouvertures.offre.modulesTexte')}</p>
        <ul className={styles.liste}>
          {MODULES_COMPLEMENTAIRES.map((module) => {
            const disponible = REGISTRE_MODULES[module].disponible;
            const coche = modules.has(module);
            const erreur = erreurs[`module-${module}`];
            const requis = dependancesManquantes(module, []);
            const dependants = modulesDependantDe(module).filter((autre) => modules.has(autre));
            return (
              <li
                key={module}
                className={`${styles.choix} ${coche ? styles.choixCoche : ''}`}
                data-module={module}
              >
                <div className={styles.choixTete}>
                  <Checkbox
                    id={`ouv-module-${module}`}
                    label={libelles.libelle(module)}
                    checked={coche}
                    disabled={!disponible || (coche && dependants.length > 0)}
                    onCheckedChange={(valeur) => basculerModule(module, valeur === true)}
                  />
                  {!disponible ? <Badge>{t('ouvertures.offre.bientot')}</Badge> : null}
                </div>
                <p className={styles.choixAide}>{libelles.description(module)}</p>
                {requis.length > 0 ? (
                  <p className={styles.choixAide}>
                    {t('ouvertures.offre.necessite', { liste: listeLibelles(requis) })}
                  </p>
                ) : null}
                {dependants.length > 0 ? (
                  <p className={styles.choixAide}>
                    {t('ouvertures.offre.requisPar', { liste: listeLibelles(dependants) })}
                  </p>
                ) : null}
                {erreur ? (
                  <p className={styles.choixAlerte} role="alert">
                    {erreur}
                  </p>
                ) : null}
              </li>
            );
          })}
        </ul>

        <hr className={styles.separateur} />

        <h3>{t('ouvertures.offre.addons')}</h3>
        <p className={styles.groupeTexte}>{t('ouvertures.offre.addonsTexte')}</p>
        <ul className={styles.liste}>{addonsLivres.map(ligneAddon)}</ul>
        {addonsAVenir.length > 0 ? (
          <details className={styles.aVenir}>
            <summary>{t('ouvertures.offre.addonsAVenir', { count: addonsAVenir.length })}</summary>
            <ul className={styles.liste}>{addonsAVenir.map(ligneAddon)}</ul>
          </details>
        ) : null}

        <hr className={styles.separateur} />

        <h3>{t('ouvertures.offre.services')}</h3>
        <p className={styles.groupeTexte}>{t('ouvertures.offre.servicesTexte')}</p>
        <ul className={styles.liste}>
          {SERVICES.map((service) => (
            <li
              key={service}
              className={`${styles.choix} ${services.has(service) ? styles.choixCoche : ''}`}
            >
              <Checkbox
                id={`ouv-service-${service}`}
                label={libelles.libelle(service)}
                checked={services.has(service)}
                onCheckedChange={(valeur) =>
                  setServices((courant) => {
                    const suivant = new Set(courant);
                    if (valeur === true) suivant.add(service);
                    else suivant.delete(service);
                    return suivant;
                  })
                }
              />
            </li>
          ))}
        </ul>

        <hr className={styles.separateur} />

        <h3>{t('ouvertures.offre.forfait')}</h3>
        <p className={styles.groupeTexte}>{t('ouvertures.offre.forfaitTexte')}</p>
        <div className={styles.groupeGrille}>
          <Champ id="ouv-forfait-texte" label={t('ouvertures.offre.forfaitDescription')}>
            <Textarea
              id="ouv-forfait-texte"
              rows={3}
              maxLength={500}
              value={forfaitTexte}
              onChange={(evenement) => setForfaitTexte(evenement.target.value)}
            />
          </Champ>
          <Champ
            id="ouv-forfait-montant"
            label={t('ouvertures.offre.forfaitMontant')}
            erreur={erreurs.forfaitMontant}
          >
            <Input
              id="ouv-forfait-montant"
              mono
              inputMode="decimal"
              value={forfaitMontant}
              onChange={(evenement) => setForfaitMontant(evenement.target.value)}
              {...descriptionChamp('ouv-forfait-montant', {
                erreur: Boolean(erreurs.forfaitMontant),
              })}
            />
          </Champ>
        </div>
      </fieldset>

      <div className={styles.actionsEtape}>
        <Button variant="ghost" onClick={() => allerA(1)}>
          {t('ouvertures.assistant.precedent')}
        </Button>
        <div>
          <Button type="submit" loading={enCours} disabled={lectureSeule}>
            {enCours
              ? t('ouvertures.assistant.enregistrement')
              : t('ouvertures.assistant.continuer')}
          </Button>
        </div>
      </div>
    </form>
  );
}
